'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { FC, ReactNode, useCallback, useState } from 'react';
import { shallow } from 'zustand/shallow';

import { CarGalleryNavigation } from 'src/constants';
import { IconCloseDoors } from 'src/general/Icons/IconCloseDoors';
import { IconExpand } from 'src/general/Icons/IconExpand';
import { IconExterior } from 'src/general/Icons/IconExterior';
import { IconInterior } from 'src/general/Icons/IconInterior';
import { IconOpenDoors } from 'src/general/Icons/IconOpenDoors';
import { AddToWatchList } from 'src/general/components/AddToWatchList/AddToWatchList';
import { Button } from 'src/general/components/Button/Button';
import { ButtonIcon } from 'src/general/components/Button/ButtonIcon';
import { Image } from 'src/general/components/Image/Image';
import { useUpdatedCarStatus } from 'src/plp/components/Results/useUpdatedCarStatus';
import { useStatusPillState } from 'src/plp/components/StatusPill/useStatusPillState';
import { useFeatureHotspotsStore } from 'src/stores/featureHotspotsStore';
import { pdpStore } from 'src/stores/pdpStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import {
  DataLayerPdpEvent,
  productDetailToListDataRow,
  pushToDataLayer,
  pushToDataLayerPDP,
} from 'src/utils/pushToDataLayer';
import styles from './CarHero.module.scss';

const CarRotator = dynamic(() => import('src/pdp/components/CarRotator/CarRotator').then((mod) => mod.CarRotator), {
  ssr: false,
});
const Interior = dynamic(() => import('src/pdp/components/Interior/Interior').then((mod) => mod.Interior), {
  ssr: false,
});

interface Props {
  productDetails: ProductDetailsPayload;
  statusPill: ReactNode;
  promoPill: ReactNode;
  hasPromo?: boolean;
  microcopy: {
    openDoorsLabel: ReactNode;
    closeDoorsLabel: ReactNode;
  };
}

export const CarHero: FC<Props> = ({ productDetails, statusPill, promoPill, hasPromo, microcopy }) => {
  const { setCurrentModal } = pdpStore(
    ({ setCurrentModal }) => ({
      setCurrentModal,
    }),
    shallow,
  );

  const { onClickFeatureHotspot, setViewType } = useFeatureHotspotsStore(
    ({ onClickFeatureHotspot, setViewType }) => ({ onClickFeatureHotspot, setViewType }),
    shallow,
  );

  const carStatus = useUpdatedCarStatus(productDetails.sku, productDetails.state);
  const [displayMode, setDisplayMode] = useState<CarGalleryNavigation>('exterior');
  const [interiorDisplayedFirstTime, setInteriorDisplayedFirstTime] = useState<boolean>(false);
  const { status } = useStatusPillState({ sku: productDetails.sku, state: carStatus });
  const showClose360Doors = useCallback(() => {
    const option = displayMode === 'interior' ? 'EXTERIOR' : 'CLOSE_DOOR';
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option,
    });
    setDisplayMode('exterior');
  }, [displayMode]);

  const showOpen360Doors = useCallback(() => {
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option: 'OPEN_DOOR',
    });
    setDisplayMode('open');
  }, []);
  const showInterior = useCallback(() => {
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option: 'INTERIOR',
    });
    setInteriorDisplayedFirstTime(true);
    setDisplayMode('interior');
  }, []);

  const onExpand = () => {
    setCurrentModal({
      name: 'gallery',
      initialState: displayMode,
    });

    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option: 'EXPAND',
    });
  };

  const handleFeatureHotspotClick = useCallback((featureName: string, section: 'exterior' | 'interior') => {
    setViewType(section);
    onClickFeatureHotspot(featureName);
    setCurrentModal({
      name: 'gallery',
      initialState: section,
    });
    pushToDataLayer({
      event: 'pdp_cta_interaction',
      value: {
        option: 'HOTSPOT',
        type: 'CLICK',
        value: featureName,
      },
    });
  }, []);

  return (
    <section className={styles.root}>
      <>
        <div className={styles.playerWrapper}>
          <div
            className={classNames(styles.interiorWrapper, {
              [styles.active]: displayMode === 'interior',
            })}
          >
            {interiorDisplayedFirstTime && (
              <Interior
                image={productDetails.images.interior[0].url}
                hotspots={productDetails.hotspots?.interior ?? []}
                onClickHotspot={(featureName) => handleFeatureHotspotClick(featureName, 'interior')}
              />
            )}
          </div>
        </div>
        {(displayMode === 'exterior' || displayMode === 'open') && (
          <>
            <Image
              src={productDetails.images['360doorsclosed'][0].url}
              className={styles.heroImage}
              width={1040}
              height={585}
              alt=""
              priority
            />
            <CarRotator
              visible
              imagesData={productDetails.images}
              displayMode={displayMode}
              hotspots={productDetails.hotspots?.exterior ?? []}
              onClickHotspot={(featureName, _) => {
                handleFeatureHotspotClick(featureName, 'exterior');
              }}
              hasPromo={hasPromo}
              hasStatus={!!status}
            />
          </>
        )}
      </>
      <aside className={styles.controls}>
        <ButtonIcon
          className={classNames(styles.controlsButton, styles.controlsButtonExpand)}
          onClick={onExpand}
          variant="tertiary"
          size="small"
          title="Expand"
          Icon={IconExpand}
          data-testid="button-expand"
        />
        {displayMode === 'exterior' && (
          <>
            <Button
              className={classNames(styles.controlsButton, styles.controlsButtonDoors, styles.controlButtonDesktop)}
              onClick={showOpen360Doors}
              variant="tertiary"
              size="small"
              startIcon={<IconOpenDoors />}
              data-testid="button-doorsOpen-desktop"
            >
              {microcopy.openDoorsLabel}
            </Button>
            <ButtonIcon
              className={classNames(styles.controlsButton, styles.controlsButtonDoors, styles.controlButtonMobile)}
              onClick={showOpen360Doors}
              variant="tertiary"
              size="small"
              title="Open doors"
              Icon={IconOpenDoors}
              data-testid="button-doorsOpen-mobile"
            />
          </>
        )}
        {displayMode === 'open' && (
          <>
            <Button
              className={classNames(styles.controlsButton, styles.controlsButtonDoors, styles.controlButtonDesktop)}
              onClick={showClose360Doors}
              variant="tertiary"
              size="small"
              startIcon={<IconCloseDoors />}
              data-testid="button-doorsClosed-desktop"
            >
              {microcopy.closeDoorsLabel}
            </Button>
            <ButtonIcon
              className={classNames(styles.controlsButton, styles.controlsButtonDoors, styles.controlButtonMobile)}
              onClick={showClose360Doors}
              variant="tertiary"
              size="small"
              title="Open doors"
              Icon={IconCloseDoors}
              data-testid="button-doorsClosed-mobile"
            />
          </>
        )}
        {(displayMode === 'exterior' || displayMode === 'open') && (
          <>
            <Button
              className={classNames(styles.controlsButton, styles.controlsButtonInterior, styles.controlButtonDesktop)}
              onClick={showInterior}
              variant="tertiary"
              size="small"
              startIcon={<IconInterior />}
              data-testid="button-interior-desktop"
            >
              Interior
            </Button>
            <ButtonIcon
              className={classNames(styles.controlsButton, styles.controlsButtonInterior, styles.controlButtonMobile)}
              onClick={showInterior}
              variant="tertiary"
              size="small"
              title="Interior"
              Icon={IconInterior}
              data-testid="button-interior-mobile"
            />
          </>
        )}
        {displayMode === 'interior' && (
          <>
            <Button
              className={classNames(styles.controlsButton, styles.controlsButtonInterior, styles.controlButtonDesktop)}
              onClick={showClose360Doors}
              variant="tertiary"
              size="small"
              startIcon={<IconExterior />}
              data-testid="button-exterior-desktop"
            >
              Exterior
            </Button>
            <ButtonIcon
              className={classNames(styles.controlsButton, styles.controlsButtonInterior, styles.controlButtonMobile)}
              onClick={showClose360Doors}
              variant="tertiary"
              size="small"
              title="Exterior"
              Icon={IconExterior}
              data-testid="button-exterior-mobile"
            />
          </>
        )}

        <AddToWatchList
          sku={productDetails.sku}
          product={productDetailToListDataRow(productDetails)}
          className={classNames(styles.controlsButton, styles.controlsButtonWatchlist)}
        />
        <div className={styles.statusWrapper}>{statusPill}</div>
        {carStatus === 'available' ? <div className={styles.promoPill}>{promoPill}</div> : null}
      </aside>
    </section>
  );
};
